import React from 'react';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatQtyForDisplay } from '../utils/decimalInput';
import { IssueSerialSelection } from '../hooks/useIssueSerials';

interface Props {
  ident?: string;
  required: number;
  selectedQuantity: number;
  difference: number;
  serials: IssueSerialSelection[];
  onQuantityChange: (id: string, value: string) => number | null;
  onRemove: (id: string) => void;
  onClear: () => void;
  onDraftChange: (dirty: boolean) => void;
}

const IssueSerialsSelectedPanel: React.FC<Props> = ({
  ident,
  required,
  selectedQuantity,
  difference,
  serials,
  onQuantityChange,
  onRemove,
  onClear,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const [active, setActive] = React.useState<string | null>(null);
  const [drafts, setDrafts] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    setDrafts(Object.fromEntries(serials.map((serial) => [
      serial.id,
      formatQtyForDisplay(serial.quantity, { showZero: true }),
    ])));
  }, [serials]);

  const commitDraft = (serial: IssueSerialSelection) => {
    const value = drafts[serial.id] ?? formatQtyForDisplay(serial.quantity, { showZero: true });
    const accepted = onQuantityChange(serial.id, value);
    onDraftChange(accepted === null);
    setDrafts((current) => ({
      ...current,
      [serial.id]: accepted === null ? value : formatQtyForDisplay(accepted, { showZero: true }),
    }));
  };

  return (
    <section className="min-w-0 border border-gray-300 rounded overflow-hidden">
      <div className="p-3 border-b bg-gray-50 flex justify-between gap-3">
        <div>
          <h3 className="font-semibold">{t('issueSerials.selectedTitle')}</h3>
          <dl className="grid grid-cols-2 gap-x-4 text-sm mt-2">
            <dt>{t('issueSerials.ident')}</dt>
            <dd>{ident || '-'}</dd>
            <dt>{t('issueSerials.requiredQuantity')}</dt>
            <dd className="text-right">{formatQtyForDisplay(required, { showZero: true })}</dd>
            <dt>{t('issueSerials.quantity')}</dt>
            <dd className="text-right">{formatQtyForDisplay(selectedQuantity, { showZero: true })}</dd>
            <dt>{t('issueSerials.difference')}</dt>
            <dd className={`text-right font-semibold ${difference !== 0 ? 'text-red-600' : ''}`}>
              {formatQtyForDisplay(difference, { showZero: true })}
            </dd>
          </dl>
        </div>
        <div className="flex gap-1">
          <button
            type="button"
            disabled={!active}
            onClick={() => active && onRemove(active)}
            className="px-2 text-sm flex items-center gap-1 hover:bg-gray-200 disabled:opacity-40"
            title={t('issueSerials.delete')}
          >
            <Trash2 size={16} />
            {t('issueSerials.delete')}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="px-2 text-sm hover:bg-gray-200"
          >
            {t('issueSerials.deleteAll')}
          </button>
        </div>
      </div>
      <div className="overflow-auto max-h-[48vh]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-gray-100">
            <tr>
              <th className="p-2 text-left">{t('issueSerials.serialNumber')}</th>
              <th className="p-2 text-right">{t('issueSerials.quantity')}</th>
              <th className="p-2 text-center">{t('issueSerials.expiryDate')}</th>
            </tr>
          </thead>
          <tbody>
            {serials.map((serial) => (
              <tr
                key={serial.id}
                onClick={() => setActive(serial.id)}
                className={active === serial.id
                  ? 'border-t cursor-pointer bg-blue-100'
                  : 'border-t cursor-pointer hover:bg-blue-50'}
              >
                <td className="p-2 font-mono">{serial.serial}</td>
                <td className="p-1">
                  <input
                    value={drafts[serial.id] ?? ''}
                    inputMode="decimal"
                    onChange={(event) => {
                      setDrafts((current) => ({ ...current, [serial.id]: event.target.value }));
                      onDraftChange(true);
                    }}
                    onBlur={() => commitDraft(serial)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                    }}
                    onClick={(event) => event.stopPropagation()}
                    className="w-full px-1 text-right border rounded outline-none focus:bg-blue-50 focus:ring-2 focus:ring-blue-500/50"
                  />
                </td>
                <td className="p-2 text-center">{serial.deadline || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default IssueSerialsSelectedPanel;
